import { useNavigate, useParams } from "@tanstack/solid-router";
import { createEffect, createSignal, Show } from "solid-js";
import { SeatFieldsForm, seatFieldsFrom } from "~/components/SeatFieldsForm";
import {
  ApiError,
  fetchNameSlot,
  submitNameSlot,
  type SeatFields,
} from "~/lib/api";
import { trackBeacon } from "~/lib/analytics";
import { cityLabel } from "~/lib/tickets";

function assignErrorCode(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === "string" && /^[a-z][a-z0-9_]{0,62}$/.test(code)) {
      return code;
    }
  }
  return "unknown";
}

export function AssignTicketPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const token = () => params().token ?? "";

  const [loading, setLoading] = createSignal(true);
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [eventName, setEventName] = createSignal("");
  const [eventId, setEventId] = createSignal("");
  const [dateLabel, setDateLabel] = createSignal("");
  const [refunded, setRefunded] = createSignal(false);
  const [saved, setSaved] = createSignal(false);
  const [fields, setFields] = createSignal<SeatFields>(seatFieldsFrom());

  createEffect(() => {
    const t = token();
    if (!t) return;
    setLoading(true);
    setError(null);
    fetchNameSlot(t)
      .then((res) => {
        setEventName(res.data.event_name);
        setEventId(res.data.event_id);
        setDateLabel(res.data.event_date_label ?? "");
        setRefunded(res.data.refunded);
        setFields(seatFieldsFrom(res.data.fields));
        trackBeacon(
          "seat_assign_opened",
          { already_assigned: res.data.assigned },
          { page: "/assign", city: res.data.city, ticketingEventId: res.data.event_id },
        );
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Could not load this assign link");
        trackBeacon("seat_assign_failed", { error: assignErrorCode(err), phase: "open" }, { page: "/assign" });
      })
      .finally(() => setLoading(false));
  });

  async function onSubmit() {
    setSaving(true);
    setError(null);
    try {
      const res = await submitNameSlot(token(), fields());
      setFields(seatFieldsFrom(res.data.fields));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
      trackBeacon(
        "seat_assign_failed",
        { error: assignErrorCode(err), phase: "submit" },
        { page: "/assign", ticketingEventId: eventId() || undefined },
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section class="card">
      <p class="status-chip">Ticket details</p>
      <h1>Assign your ticket</h1>
      <Show when={eventName()}>
        <p>
          <strong>{eventName()}</strong>
          <Show when={dateLabel()}>
            <span class="muted"> · {dateLabel()}</span>
          </Show>
        </p>
      </Show>
      <Show when={eventId()}>
        <p class="muted">{cityLabel(eventId())}</p>
      </Show>

      <Show when={loading()}>
        <p class="muted">Loading…</p>
      </Show>
      <Show when={!loading() && refunded()}>
        <div class="error-box">This order was refunded. Seats cannot be updated.</div>
      </Show>
      <Show when={!loading() && !refunded() && error() && !eventName()}>
        <div class="error-box">{error()}</div>
      </Show>
      <Show when={!loading() && !refunded() && eventName()}>
        <SeatFieldsForm
          value={fields()}
          onChange={setFields}
          onSubmit={onSubmit}
          saving={saving()}
          error={error()}
          submitLabel="Save ticket details"
        />
      </Show>

      <Show when={saved()}>
        <div class="support-box" style={{ "margin-top": "16px" }}>
          <p>
            Saved. Sign in to Connect to claim this ticket for the directory and badge.{" "}
            <a href="/help#claim">How claiming works</a>.
          </p>
          <button
            type="button"
            class="btn"
            onClick={() =>
              navigate({
                to: "/onboarding/$eventId",
                params: { eventId: eventId() },
              })
            }
          >
            Continue to claim
          </button>
        </div>
      </Show>
    </section>
  );
}
